import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import { 
  Phone, PhoneCall, Clock, Users, DollarSign, CheckCircle, ArrowRight,
  Bot, Zap, Calendar, MessageSquare, Shield, Star,
  Thermometer, Wind, Snowflake, Flame, Wrench, AlertTriangle, Sun, Home
} from "lucide-react";

const AIReceptionistHVACPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "AI Receptionist for HVAC Companies Dallas | HVAC Virtual Receptionist | 24/7 HVAC Answering Service DFW";
  }, []);

  const stats = [
    { value: "24/7", label: "Emergency Dispatch", icon: Clock },
    { value: "Every Call", label: "Answered & Booked", icon: PhoneCall },
    { value: "<30s", label: "Response Time", icon: Zap },
    { value: "$0", label: "Missed Emergency Calls", icon: DollarSign }
  ];

  const hvacServices = [
    { icon: Snowflake, name: "AC Repair & Install", desc: "Emergency breakdowns, new system quotes, maintenance scheduling" },
    { icon: Flame, name: "Heating Services", desc: "Furnace repairs, heat pump issues, no-heat emergencies" },
    { icon: Wind, name: "Ventilation & Ductwork", desc: "Air quality inquiries, duct cleaning, system upgrades" },
    { icon: Thermometer, name: "Maintenance Plans", desc: "Seasonal tune-ups, membership sign-ups, renewal reminders" },
    { icon: AlertTriangle, name: "Emergency Services", desc: "24/7 urgent repairs, gas leaks, system failures" },
    { icon: Home, name: "New Construction", desc: "Builder inquiries, system design consultations, project scheduling" }
  ];

  const features = [
    { 
      icon: AlertTriangle, 
      title: "Emergency Call Dispatch", 
      desc: "Instantly routes urgent calls—no AC in 100°F heat, no heat in winter, or gas smells. Gets techs dispatched in minutes, not hours." 
    },
    { 
      icon: Calendar, 
      title: "Smart Scheduling", 
      desc: "Books service calls into your dispatch software. Considers service areas, tech specialties, and job durations. Fills schedule gaps automatically." 
    },
    { 
      icon: DollarSign, 
      title: "Quote Capture", 
      desc: "Collects system details, square footage, and needs for new installs. Sends qualified leads with full info for accurate estimates." 
    },
    { 
      icon: MessageSquare, 
      title: "Service Reminders", 
      desc: "Proactively calls maintenance plan customers for seasonal tune-ups. Fills slow seasons with preventive maintenance work." 
    },
    { 
      icon: Shield, 
      title: "After-Hours Triage", 
      desc: "Determines true emergencies vs. next-day calls. Reduces unnecessary after-hours dispatches while never missing real emergencies." 
    },
    { 
      icon: Wrench, 
      title: "Warranty & Parts Tracking", 
      desc: "Answers warranty questions, schedules warranty repairs, and captures details for parts ordering before the tech arrives." 
    }
  ];

  const comparisons = [
    { item: "Dispatcher/Receptionist", traditional: "$35,000-$45,000/year", ai: "$199-$399/month" },
    { item: "After-Hours Service", traditional: "$15-25/call extra", ai: "Included 24/7" },
    { item: "Missed Emergency Calls", traditional: "Lost revenue", ai: "100% answered" },
    { item: "Seasonal Surge Handling", traditional: "Overwhelmed/missed", ai: "Unlimited capacity" },
    { item: "Spanish-Speaking Callers", traditional: "Limited/None", ai: "Fluent Spanish" },
    { item: "Dispatch Integration", traditional: "Manual entry", ai: "ServiceTitan, Housecall Pro" }
  ];

  const faqs = [
    { 
      q: "How does AI handle HVAC emergency calls at 2 AM?", 
      a: "Our AI immediately recognizes emergency situations—no AC in extreme heat, no heat in freezing temps, gas odors, or carbon monoxide alarms. It captures the address, system symptoms, and customer contact info, then instantly alerts your on-call technician via text, call, and app notification. The tech gets full details before calling back, and dispatch happens in minutes, not hours. Non-emergencies are scheduled for next-day service."
    },
    { 
      q: "Can the AI handle seasonal call surges?",
      a: "Absolutely. During the first heat wave or cold snap, call volume can spike 300-400%. Human staff gets overwhelmed, and callers get voicemail or hang up. AI handles unlimited simultaneous calls—every single one answered immediately. No busy signals, no hold times, no lost customers to competitors. It books service calls while managing your schedule in real-time."
    },
    { 
      q: "Does it integrate with ServiceTitan or Housecall Pro?",
      a: "Yes. We integrate with ServiceTitan, Housecall Pro, FieldEdge, Service Fusion, and other major HVAC dispatch platforms. The AI sees your real-time availability, creates jobs, assigns to appropriate techs based on skills and location, and updates your system automatically. No double-entry, no scheduling conflicts."
    },
    { 
      q: "How does AI qualify leads for new system installations?",
      a: "For replacement or new install inquiries, the AI gathers crucial information: current system type/age, home square footage, recent energy bills, cooling/heating concerns, timeline, and budget range. Your sales team receives qualified leads with all details needed for accurate estimates. Tire-kickers are filtered out, saving your comfort advisors' time for serious buyers."
    },
    { 
      q: "Can it manage maintenance agreement customers?",
      a: "Yes, proactively. The AI can call your maintenance plan members when their seasonal tune-up is due, book the appointment, and update your system. This fills schedule gaps during slow seasons and improves customer retention. It also handles renewal reminders and can even upsell maintenance plans to service call customers."
    },
    { 
      q: "What about complex technical questions?",
      a: "The AI handles common questions—pricing ranges, service areas, hours, equipment brands you service, financing options, and warranty policies. For technical questions beyond basic FAQs, it captures the question, takes customer details, and schedules a callback from your technical team with full context. Customers appreciate immediate acknowledgment even when they need a specialist."
    }
  ];

  const generateSchemas = () => {
    return [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "AI Receptionist for HVAC Companies",
        "description": "24/7 AI-powered virtual receptionist for HVAC companies in Dallas-Fort Worth. Emergency dispatch, service scheduling, lead qualification, and seasonal surge handling.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Digital Media Ninja",
          "address": { "@type": "PostalAddress", "streetAddress": "3333 Harry Hines Blvd", "addressLocality": "Dallas", "addressRegion": "TX", "postalCode": "75201" }
        },
        "areaServed": ["Dallas", "Fort Worth", "Plano", "Arlington", "Frisco", "McKinney", "Irving"],
        "serviceType": "AI Virtual Receptionist for HVAC"
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "AI HVAC Receptionist Service",
        "offers": { "@type": "AggregateOffer", "lowPrice": "199", "highPrice": "399", "priceCurrency": "USD" },
      }
    ];
  };

  const schemas = generateSchemas();

  return (
    <div className="min-h-screen bg-black" data-testid="ai-receptionist-hvac-page">
      <Helmet>
        <title>AI Receptionist for HVAC Companies Dallas | 24/7 HVAC Answering Service | Virtual Dispatcher DFW</title>
        <meta name="description" content="AI receptionist for Dallas HVAC companies. 24/7 emergency dispatch, service scheduling, lead qualification. ServiceTitan & Housecall Pro integration. Handle seasonal surges. Free demo!" />
        <meta name="keywords" content="AI receptionist HVAC Dallas, HVAC answering service, HVAC virtual receptionist DFW, 24/7 HVAC dispatch, HVAC phone answering, heating cooling answering service, ServiceTitan integration AI" />
        <link rel="canonical" href="https://digitalmedianinja.com/ai-receptionist-hvac" />
        <meta property="og:title" content="AI Receptionist for HVAC Companies Dallas | 24/7 Dispatch" />
        <meta property="og:description" content="Never miss an HVAC emergency. AI handles calls 24/7, dispatches techs instantly, books service calls automatically." />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        {schemas.map((schema, idx) => (
          <script key={idx} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-600/20 via-transparent to-orange-900/10"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full">
                <Thermometer className="w-4 h-4 text-orange-400" />
                <span className="text-orange-400 text-sm font-medium">Built for HVAC Contractors</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                AI Receptionist for <span className="text-orange-400">HVAC Companies</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-xl">
                Never miss an emergency call. AI dispatches techs 24/7, handles seasonal surges, and books service calls—even when your phones would normally explode.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <BookConsultationButton 
                  meetingType="hvac"
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-black font-semibold px-8"
                >
                  Schedule Demo <ArrowRight className="w-5 h-5 ml-2" />
                </BookConsultationButton>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <Phone className="w-5 h-5 mr-2" /> Call Us Now
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <Card className="bg-white/5 border-orange-500/20 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center">
                      <Thermometer className="w-8 h-8 text-orange-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">HVAC Dispatch AI</h3>
                      <p className="text-orange-400">24/7 Emergency Ready</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {["Instant emergency dispatch at any hour", "ServiceTitan & Housecall Pro integration", "Unlimited call capacity for surges", "Spanish-speaking support included"].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-white/10">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-8 h-8 text-orange-400 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HVAC Services */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">HVAC Services We Support</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">AI trained for every aspect of heating, cooling, and ventilation business.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {hvacServices.map((service, i) => (
              <Card key={i} className="bg-white/5 border-white/10 hover:border-orange-500/50 transition-all">
                <CardContent className="p-6">
                  <service.icon className="w-10 h-10 text-orange-400 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{service.name}</h3>
                  <p className="text-gray-400">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white/5">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Features for HVAC Contractors</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="p-6 rounded-xl bg-black/50 border border-white/10">
                <feature.icon className="w-10 h-10 text-orange-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">HVAC Dispatch Cost Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 text-gray-400">Expense</th>
                  <th className="text-center py-4 px-6 text-gray-400">Traditional</th>
                  <th className="text-center py-4 px-6 text-orange-400">AI Receptionist</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, i) => (
                  <tr key={i} className="border-b border-white/10">
                    <td className="py-4 px-6 text-white">{row.item}</td>
                    <td className="py-4 px-6 text-center text-gray-400">{row.traditional}</td>
                    <td className="py-4 px-6 text-center text-orange-400 font-semibold">{row.ai}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-white/10 rounded-lg overflow-hidden">
                <button className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/5" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="text-white font-medium">{faq.q}</span>
                  <ArrowRight className={`w-5 h-5 text-orange-400 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-4"><p className="text-gray-400">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learn More Section */}
      <section className="py-16 border-t border-white/10">
        <div className="container-custom">
          <div className="bg-gradient-to-r from-orange-500/10 to-orange-600/5 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Complete Guide to AI Receptionists for HVAC Companies
                </h2>
                <p className="text-gray-300 mb-6">
                  Learn everything about implementing AI reception for your HVAC business—from handling seasonal surges
                  to integrating with ServiceTitan and Housecall Pro, plus ROI calculations during peak demand.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-orange-400" />
                    Seasonal surge handling
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-orange-400" />
                    Field service software integrations
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-orange-400" />
                    Emergency dispatch automation
                  </li>
                </ul>
                <Link to="/blog/ai-receptionist-for-hvac-companies">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-black font-semibold">
                    Read the Complete Guide <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="hidden md:block">
                <div className="bg-white/5 rounded-xl p-6 border border-orange-500/20">
                  <div className="flex items-center gap-3 mb-4">
                    <Thermometer className="w-8 h-8 text-orange-400" />
                    <div>
                      <div className="text-white font-semibold">14 Minute Read</div>
                      <div className="text-gray-400 text-sm">Comprehensive Guide</div>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm text-gray-400">
                    <div>✓ Emergency vs Non-Emergency Triage</div>
                    <div>✓ Maintenance Agreement Sales</div>
                    <div>✓ Peak Season ROI Calculator</div>
                    <div>✓ FAQ Section with Schema</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-600/20 to-orange-900/20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Handle Every HVAC Call?</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8">Join Dallas HVAC companies using AI to capture more service calls and never miss an emergency.</p>
          <BookConsultationButton meetingType="aiReceptionist" size="lg" className="bg-orange-500 hover:bg-orange-600 text-black font-semibold px-8">
            Schedule Your Free Demo
          </BookConsultationButton>
        </div>
      </section>
    </div>
  );
};

export default AIReceptionistHVACPage;
